import { ReactNode } from 'react';
import { AgencyAddon, AddonCode } from '@/apis/agency/addons/types';
import AddonsPrioritySupportIcon from '@/assets/icons/AddonsPrioritySupportIcon';
import AddonsSMSIcon from '@/assets/icons/AddonsSMSIcon';
import AddonsA2pIcon from '@/assets/icons/AddonsA2pIcon';
import { MdOutlineAutoAwesome } from 'react-icons/md';
import AddonsWorkflowIcon from '@/assets/icons/AddonsWorkflowIcon';

export const COMMITMENT_OPTIONS = [
  { value: 0, label: 'No Commitment' },
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '12 Months' },
  { value: 24, label: '24 Months' }
];

const DISPLAY_TITLES: Record<string, string> = {
  priority_support: 'Priority Support',
  sms_hosting: 'SMS on Existing Business Number',
  a2p_10dlc: 'A2P 10DLC Registration',
  ai_workflow: 'AI Workflows'
};

const SHORT_BLURBS: Record<string, string> = {
  priority_support:
    'Priority handling on every support request, with immediate support during non-working hours and weekends. Billed per location.',
  sms_hosting:
    'Enable SMS on your existing business number without changing your voice provider. Billed per hosted number.',
  a2p_10dlc: 'A2P 10DLC brand and campaign registration so your messages reach US carriers without filtering.',
  ai_workflow:
    'AI-powered workflows that handle repetitive admin tasks across all locations, with a monthly run allowance and simple per-run pricing.'
};

const FIELD_LABELS: Record<string, Record<string, string>> = {
  priority_support: {
    one_time_setup_fee: 'One-Time Setup Fee',
    price_per_location: 'Price Per Location (Monthly)',
    minimum_locations_billed: 'Minimum Locations Billed'
  },
  sms_hosting: {
    one_time_setup_fee: 'One-Time Setup Fee',
    price_per_hosted_number: 'Price Per Hosted Number (Monthly)'
  },
  a2p_10dlc: {
    one_time_setup_fee: 'One-Time Setup Fee',
    vetting_fee_per_campaign: 'Vetting Fee Per Campaign',
    mixed_campaign_per_month: 'Mixed Campaign',
    low_volume_mixed_campaign: 'Low Volume Mixed Campaign',
    max_numbers_per_campaign: 'Max Numbers per Campaign'
  },
  ai_workflow: {
    one_time_setup_fee: 'One-Time Setup Fee',
    monthly_price_all_locations: 'Monthly Price (All Locations)',
    included_runs_per_month: 'Included Runs / Month',
    price_per_additional_run: 'Price Per Additional Run'
  }
};

const FIELD_HELPERS: Record<string, Record<string, string>> = {
  a2p_10dlc: {
    vetting_fee_per_campaign: 'Charged for each campaign submission and resubmission.',
    mixed_campaign_per_month: 'Recommended for high-volume campaigns. It costs $20/month through Bandwidth.',
    low_volume_mixed_campaign:
      'Recommended for small businesses with lower messaging volumes. It costs $3/month through Bandwidth.'
  }
};

const MONEY_KEYS = new Set([
  'one_time_setup_fee',
  'price_per_location',
  'price_per_hosted_number',
  'vetting_fee_per_campaign',
  'mixed_campaign_per_month',
  'low_volume_mixed_campaign',
  'monthly_price_all_locations',
  'price_per_additional_run'
]);

export function getAddonDisplayTitle(addon: AgencyAddon): string {
  return DISPLAY_TITLES[addon.code] || addon.name;
}

export function getAddonShortBlurb(addon: AgencyAddon): string {
  return SHORT_BLURBS[addon.code] || addon.description || '';
}

export function getAddonFieldLabel(code: AddonCode, key: string, fallback?: string): string {
  return FIELD_LABELS[code]?.[key] || fallback || key;
}

export function getAddonFieldHelper(code: AddonCode, key: string): string | undefined {
  return FIELD_HELPERS[code]?.[key];
}

export function isMoneyPriceKey(key: string): boolean {
  return MONEY_KEYS.has(key);
}

export function formatCommitmentLabel(months?: number | null): string {
  const value = Number(months) || 0;
  if (value <= 0) return 'No Commitment';
  return `${value} Months Commitment`;
}

function money(value: number | string | undefined): string {
  const amount = Number(value) || 0;
  // Match design: whole dollars as $50, fractional as $0.10
  if (Number.isInteger(amount)) return `$${amount}`;
  return `$${amount.toFixed(2)}`;
}

/** Structured badge parts so amount/unit can use different typography. */
export type AddonPriceBadgePiece =
  | { type: 'segment'; amount: string; unit: string; emphasizeAmount?: boolean }
  | { type: 'dot' }
  | { type: 'or' };

function segment(amount: string, unit: string, emphasizeAmount = true): AddonPriceBadgePiece {
  return { type: 'segment', amount, unit, emphasizeAmount };
}

export function getAddonPriceBadgePieces(addon: AgencyAddon): AddonPriceBadgePiece[] {
  const prices = addon.prices || {};

  switch (addon.code) {
    case 'priority_support':
      // Listing badge shows monthly rate only (setup fee is edited in the drawer).
      return [segment(money(prices.price_per_location), ' /Location/Month')];
    case 'sms_hosting':
      return [segment(money(prices.price_per_hosted_number), ' /Hosted Number/Month')];
    case 'a2p_10dlc':
      return [
        segment(money(prices.one_time_setup_fee), ' One-time'),
        { type: 'dot' },
        segment(money(prices.mixed_campaign_per_month), ' High-Volume'),
        { type: 'or' },
        { type: 'dot' },
        segment(money(prices.low_volume_mixed_campaign), ' Low-Volume/ Per Campaign/Month')
      ];
    case 'ai_workflow': {
      const includedRuns = Number(prices.included_runs_per_month) || 0;
      // Only the monthly price is emphasized; runs + per-run stay normal weight.
      return [
        segment(money(prices.monthly_price_all_locations), ' /Month'),
        { type: 'dot' },
        segment(`${includedRuns.toLocaleString()} runs`, ' included', false),
        { type: 'dot' },
        segment(money(prices.price_per_additional_run), ' per extra run', false)
      ];
    }
    default:
      return Object.entries(prices).flatMap(([key, value], index) => {
        const pieces: AddonPriceBadgePiece[] = [];
        if (index > 0) pieces.push({ type: 'dot' });
        pieces.push(segment(isMoneyPriceKey(key) ? money(value) : String(value), ` ${key}`));
        return pieces;
      });
  }
}

export function formatAddonPriceSummary(addon: AgencyAddon): string {
  return getAddonPriceBadgePieces(addon)
    .map((piece) => {
      if (piece.type === 'dot') return ' · ';
      if (piece.type === 'or') return ' or ';
      return `${piece.amount}${piece.unit}`;
    })
    .join('');
}

export function getAddonIcon(code: AddonCode): ReactNode {
  const size = 28;
  switch (code) {
    case 'priority_support':
      return <AddonsPrioritySupportIcon />;
    case 'sms_hosting':
      return <AddonsSMSIcon />;
    case 'a2p_10dlc':
      return <AddonsA2pIcon />;
    case 'ai_workflow':
      return <AddonsWorkflowIcon />;
    default:
      return <MdOutlineAutoAwesome size={size} />;
  }
}

/** Stable order matching the product catalog / mockups. */
export const ADDON_DISPLAY_ORDER = ['priority_support', 'sms_hosting', 'a2p_10dlc', 'ai_workflow'];

export function sortAddonsForDisplay(addons: AgencyAddon[]): AgencyAddon[] {
  return [...addons].sort((a, b) => {
    const ai = ADDON_DISPLAY_ORDER.indexOf(a.code);
    const bi = ADDON_DISPLAY_ORDER.indexOf(b.code);
    const aRank = ai === -1 ? ADDON_DISPLAY_ORDER.length : ai;
    const bRank = bi === -1 ? ADDON_DISPLAY_ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return a.name.localeCompare(b.name);
  });
}
